import React, { useState, useEffect, useRef } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';

const MapComponent = ({ libraries, onLibrarySelect, selectedLibrary }) => {
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const mapRef = useRef(null);

  useEffect(() => {
    if (mapRef.current && !map) {
      // Initialize Google Map centered on Puerto Rico
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: 18.2208, lng: -66.5901 }, // Center of Puerto Rico
        zoom: 9,
        mapTypeId: 'roadmap',
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      setMap(newMap);
    }
  }, [map]);

  useEffect(() => {
    if (map && libraries && libraries.length >= 0) {
      console.log(`MapView: Updating markers for ${libraries.length} libraries`);
      
      // Clear existing markers
      markers.forEach(marker => marker.setMap(null));
      
      const newMarkers = [];
      const infoWindow = new window.google.maps.InfoWindow({
        maxWidth: 350
      });

      let markersCreated = 0;
      const totalLibraries = libraries.length;
      
      // If no libraries, just clear markers and return
      if (totalLibraries === 0) {
        console.log('MapView: No libraries to display, clearing all markers');
        setMarkers([]);
        return;
      }

      libraries.forEach((library, index) => {
        // Priority 1: Try to extract coordinates from location_link (Google Maps URL)
        let coordinatesFromLink = null;
        if (library.location_link) {
          // Try to extract coordinates from Google Maps URL
          // Format: https://maps.google.com/?q=18.2208,-66.5901 or similar
          const coordMatch = library.location_link.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
          const qMatch = library.location_link.match(/q=(-?\d+\.\d+),(-?\d+\.\d+)/);
          
          if (coordMatch) {
            coordinatesFromLink = {
              lat: parseFloat(coordMatch[1]),
              lng: parseFloat(coordMatch[2])
            };
          } else if (qMatch) {
            coordinatesFromLink = {
              lat: parseFloat(qMatch[1]),
              lng: parseFloat(qMatch[2])
            };
          }
        }
        
        // Function to create marker and info window
        const createMarkerAndInfo = (position) => {
          // Create marker with custom icon based on library type
          const marker = new window.google.maps.Marker({
            position: position,
            map: map,
            title: library.official_name || library.name,
            icon: getLibraryIcon(library.library_type),
            clickable: true,
            optimized: false
          });

          // Create info window content
          const infoContent = `
            <div style="max-width: 380px; font-family: Arial, sans-serif;">
              <div style="border-bottom: 2px solid #ff6b35; padding-bottom: 12px; margin-bottom: 15px;">
                <h3 style="margin: 0 0 8px 0; color: #ff6b35; font-size: 18px; font-weight: bold;">
                  ${library.official_name || library.name}
                </h3>
                ${library.community_name && library.community_name !== (library.official_name || library.name) ? `
                  <p style="margin: 0; font-weight: 600; color: #666; font-style: italic; font-size: 15px;">
                    "${library.community_name}"
                  </p>
                ` : ''}
              </div>

              ${library.operating_hours || library.schedule ? `
                <div style="margin: 12px 0; padding: 10px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #28a745;">
                  <div style="font-weight: bold; color: #333; margin-bottom: 5px; display: flex; align-items: center;">
                    🕐 Días y Horarios
                  </div>
                  <div style="color: #666; font-size: 14px; line-height: 1.4;">
                    ${library.operating_hours || library.schedule}
                  </div>
                </div>
              ` : ''}

              <div style="margin: 12px 0;">
                ${library.phone && library.phone !== 'n/a' && library.phone !== 'None' ? `
                  <div style="margin: 8px 0; display: flex; align-items: center;">
                    <span style="background: #28a745; color: white; padding: 6px 10px; border-radius: 15px; font-size: 13px; font-weight: bold; margin-right: 10px;">
                      📞
                    </span>
                    <a href="tel:${library.phone}" style="color: #28a745; text-decoration: none; font-weight: 600;">
                      ${library.phone}
                    </a>
                  </div>
                ` : ''}
                
                ${library.email && library.email !== 'n/a' && library.email !== 'None' ? `
                  <div style="margin: 8px 0; display: flex; align-items: center;">
                    <span style="background: #007bff; color: white; padding: 6px 10px; border-radius: 15px; font-size: 13px; font-weight: bold; margin-right: 10px;">
                      📧
                    </span>
                    <a href="mailto:${library.email}" style="color: #007bff; text-decoration: none; font-weight: 600; word-break: break-word;">
                      ${library.email}
                    </a>
                  </div>
                ` : ''}
                
                ${library.website && library.website !== 'n/a' && library.website !== 'None' && !library.website.toLowerCase().includes('no tenemos') ? `
                  <div style="margin: 8px 0; display: flex; align-items: center;">
                    <span style="background: #6f42c1; color: white; padding: 6px 10px; border-radius: 15px; font-size: 13px; font-weight: bold; margin-right: 10px;">
                      🌐
                    </span>
                    <a href="${library.website}" target="_blank" rel="noopener noreferrer" style="color: #6f42c1; text-decoration: none; font-weight: 600; word-break: break-word;">
                      Sitio Web
                    </a>
                  </div>
                ` : ''}

                ${library.social_media && typeof library.social_media === 'object' && Object.keys(library.social_media).length > 0 ? `
                  <div style="margin: 8px 0; display: flex; align-items: center;">
                    <span style="background: #e91e63; color: white; padding: 6px 10px; border-radius: 15px; font-size: 13px; font-weight: bold; margin-right: 10px;">
                      📱
                    </span>
                    <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                      ${Object.entries(library.social_media).map(([platform, url]) => {
                        if (url && url !== 'n/a' && url !== 'None') {
                          const platformName = platform === 'general' ? 'Redes Sociales' : 
                                             platform === 'facebook' ? 'Facebook' :
                                             platform === 'instagram' ? 'Instagram' :
                                             platform === 'twitter' ? 'Twitter' : platform;
                          return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #e91e63; text-decoration: none; font-weight: 600;">${platformName}</a>`;
                        }
                        return '';
                      }).filter(link => link !== '').join(' • ')}
                    </div>
                  </div>
                ` : ''}
              </div>

              <div style="text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #dee2e6;">
                <button onclick="window.viewLibraryDetails('${library.id || library.official_name}')" 
                        style="background: linear-gradient(135deg, #ff6b35, #ff8c42); color: white; border: none; padding: 12px 24px; border-radius: 25px; cursor: pointer; font-weight: bold; font-size: 14px; box-shadow: 0 3px 10px rgba(255, 107, 53, 0.3); transition: all 0.2s;"
                        onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 5px 15px rgba(255, 107, 53, 0.4)'"
                        onmouseout="this.style.transform='translateY(0px)'; this.style.boxShadow='0 3px 10px rgba(255, 107, 53, 0.3)'">
                  Ver Detalles Completos →
                </button>
              </div>
            </div>
          `;

          // Add click listener for preview popup
          marker.addListener('click', () => {
            try {
              console.log('Marker clicked:', library.official_name || library.name);
              infoWindow.close();
              infoWindow.setContent(infoContent);
              infoWindow.open({
                anchor: marker,
                map: map
              });
              window.currentLibraryForDetails = library;
            } catch (error) {
              console.error('Error handling marker click:', error);
            }
          });

          // Add double-click handler as fallback
          marker.addListener('dblclick', () => {
            console.log('Marker double-clicked:', library.official_name || library.name);
            if (onLibrarySelect) {
              onLibrarySelect(library);
            }
          });

          // Add to markers array
          newMarkers.push(marker);
        };
        
        // If we have coordinates from link, create marker directly
        if (coordinatesFromLink && !isNaN(coordinatesFromLink.lat) && !isNaN(coordinatesFromLink.lng)) {
          const position = new window.google.maps.LatLng(coordinatesFromLink.lat, coordinatesFromLink.lng);
          createMarkerAndInfo(position);
          console.log(`✅ Created marker for ${library.official_name} using location_link coordinates`);
        } else {
          // Priority 2: Use geocoding with Plus Code, then address
          const addresses = [
            library.plus_code,
            library.physical_address,
            `${library.official_name || library.name}, ${library.municipality}, Puerto Rico`
          ].filter(addr => addr && addr !== 'n/a' && addr !== 'None' && addr.trim() !== '');
          
          if (addresses.length > 0) {
            const geocoder = new window.google.maps.Geocoder();
            
            // Try geocoding with first available address
            geocoder.geocode({ 
              address: addresses[0].includes('Puerto Rico') ? addresses[0] : `${addresses[0]}, Puerto Rico`
            }, (results, status) => {
              if (status === 'OK' && results[0]) {
                const position = results[0].geometry.location;
                createMarkerAndInfo(position);
                console.log(`✅ Created marker for ${library.official_name} using geocoding (${addresses[0]})`);
              } else {
                console.warn(`⚠️ Geocoding failed for ${library.official_name}:`, status);
              }
            });
          } else {
            console.warn(`⚠️ No location data found for ${library.official_name}`);
          }
        }
      });
      
      // Log marker creation progress
      setTimeout(() => {
        console.log(`Created ${newMarkers.length} markers on map`);
      }, 2000);

      setMarkers(newMarkers);
    }
  }, [map, libraries, selectedLibrary, onLibrarySelect]);

  const getLibraryIcon = (type) => {
    const baseIcon = {
      scaledSize: new window.google.maps.Size(40, 40),
      anchor: new window.google.maps.Point(20, 40)
    };

    switch (type) {
      case 'Municipal':
        return { ...baseIcon, url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' };
      case 'Universitaria':
        return { ...baseIcon, url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png' };
      case 'Especializada':
        return { ...baseIcon, url: 'https://maps.google.com/mapfiles/ms/icons/purple-dot.png' };
      case 'Comunitaria':
        return { ...baseIcon, url: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png' };
      case 'Electrónica/Virtual':
        return { ...baseIcon, url: 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png' };
      default:
        return { ...baseIcon, url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' };
    }
  };

  return <div ref={mapRef} style={{ width: '100%', height: '600px' }} />;
};

const render = (status) => {
  console.log('Google Maps Status:', status, 'API Key present:', !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
  if (status === Status.LOADING) return <div style={{ padding: '2rem', textAlign: 'center' }}>🗺️ Cargando mapa...</div>;
  if (status === Status.FAILURE) return <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>❌ Error cargando el mapa. Por favor verifica la API key de Google Maps.</div>;
  return null;
};

const MapView = ({ libraries, onLibrarySelect, selectedLibrary }) => {
  // Set up global function for "View Details" button
  React.useEffect(() => {
    window.viewLibraryDetails = (libraryId) => {
      const library = window.currentLibraryForDetails;
      if (library && onLibrarySelect) {
        console.log('Navigating to detailed view for:', library.name);
        onLibrarySelect(library);
      } else {
        console.warn('No library data available for detailed view');
      }
    };

    // Cleanup function
    return () => {
      delete window.viewLibraryDetails;
      delete window.currentLibraryForDetails;
    };
  }, [onLibrarySelect]);

  return (
    <div className="map-view">
      <div className="map-header">
        <h2>Mapa de Bibliotecas de Puerto Rico</h2>
        <p style={{ color: '#000000', fontWeight: '600' }}>📍 {libraries.length} bibliotecas • Haz clic en un marcador para ver vista previa</p>
      </div>
      
      <div className="map-legend">
        <div className="legend-item">
          <span className="legend-dot municipal"></span>Municipal
        </div>
        <div className="legend-item">
          <span className="legend-dot universitaria"></span>Universitaria
        </div>
        <div className="legend-item">
          <span className="legend-dot especializada"></span>Especializada
        </div>
        <div className="legend-item">
          <span className="legend-dot comunitaria"></span>Comunitaria
        </div>
        <div className="legend-item">
          <span className="legend-dot electronica"></span>Electrónica/Virtual
        </div>
        <div className="legend-item">
          <span className="legend-dot other"></span>Otros
        </div>
      </div>

      <Wrapper apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB84brvNK93GCxgt7RDfVjtVg2FZq-IaQU'} render={render}>
        <MapComponent 
          libraries={libraries} 
          onLibrarySelect={onLibrarySelect}
          selectedLibrary={selectedLibrary}
        />
      </Wrapper>
    </div>
  );
};

export default MapView;